import { IsStringEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { REPORTS_CREATE, REPORTS_DELETE, REPORTS_READONLY, REPORTS_UPDATE, SERVER_NONE_VALUE } from '../Constants'
import { formToPermissionsMap, RolePermissionsFieldNames } from '../FieldNames'
import { ApplicationUserFormData, FormRolePermissions, RolePermissions } from '../Types'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import { BackendPermissionNames, ROLE_ACTIONS } from '../../Common/Constants/Permissions'

type Permission = {
    name: string
    action: string
}

const allActions = (): Record<string, boolean> => ({
    Read: false,
    Create: false,
    Update: false,
    Delete: false,
    Allowed: false
})

// Take a permissions list from the API and convert it to the form values.
export const useMapPermissionsToFormValues = (permissionsList?: string[]): FormRolePermissions => {
    const rolePermissions: Record<string, Record<string, boolean>> = Object.values(RolePermissionsFieldNames).reduce(
        (
            accum: Record<string, Record<string, boolean>>,
            fieldName: string
        ): Record<string, Record<string, boolean>> => {
            accum[fieldName] = allActions()

            return accum
        },
        {}
    )

    permissionsList
        ?.map((permission: string): Permission => {
            const [name, action]: string[] = permission.split('|')
            let finalAction: string = action

            if (IsStringEmpty(finalAction)) {
                finalAction = ROLE_ACTIONS.ALLOWED
            }

            return {
                name,
                action: finalAction
            }
        })
        ?.forEach((permission: Permission): void => {
            rolePermissions[formToPermissionsMap.val(permission?.name)][permission.action] = true
        })

    return rolePermissions
}

// Map the form values to the server required permissions values.
export const mapValuesToPermissions = (permissions?: FormRolePermissions): string[] => {
    if (permissions) {
        return Object.entries(permissions)
            .map(([key, value]: [string, Definable<RolePermissions>]): string[] => {
                const backendKeys: Definable<string[]> = ([] as string[]).concat(formToPermissionsMap.key(key))

                if (backendKeys && value) {
                    return backendKeys.reduce(
                        (accum: string[], backendKey: string): string[] =>
                            accum.concat(
                                Object.entries(value)
                                    .map(([subKey, subValue]): Definable<string> => {
                                        if (subValue) {
                                            return `${backendKey}${subKey === ROLE_ACTIONS.ALLOWED ? '' : `|${subKey}`}`
                                        }

                                        return undefined
                                    })
                                    .filter((str: Definable<string>) => !!str) as string[]
                            ),
                        []
                    )
                }

                return []
            })
            .reduce((accum: string[], next: string[]) => accum.concat(next))
    }

    return []
}

// Map non admin permissions to their server values.
export const mapNonAdminPermissions = (state: ApplicationUserFormData): string[] => {
    const nonAdminPermissions: string[] = []

    const reportingLevel: Nullable<string> = state.reporting?.reportinglevel

    if (state.reporting?.enhancedfeatures) {
        nonAdminPermissions.push(BackendPermissionNames.ENHANCED_FEATURES)
    }

    if (state.reporting?.reportingapi) {
        nonAdminPermissions.push(BackendPermissionNames.REPORTING_API)
    }

    if (reportingLevel && reportingLevel !== SERVER_NONE_VALUE && state.reporting?.readonly) {
        nonAdminPermissions.push(REPORTS_READONLY)
    } else if (reportingLevel && reportingLevel !== SERVER_NONE_VALUE) {
        nonAdminPermissions.push(REPORTS_READONLY)
        nonAdminPermissions.push(REPORTS_UPDATE)
        nonAdminPermissions.push(REPORTS_CREATE)
        nonAdminPermissions.push(REPORTS_DELETE)
    }

    if (state.reporting?.reportTemplateUser) {
        nonAdminPermissions.push(BackendPermissionNames.REPORT_TEMPLATE_USER)
    }

    return nonAdminPermissions
}

// Map other permissiosn values to their server values.
export const mapOtherPermissions = (state: ApplicationUserFormData): string[] => {
    const otherPermissions: string[] = []

    if (state.settings?.concurrentsignins) {
        otherPermissions.push(BackendPermissionNames.CONCURRENT_SIGNINS)
    }

    if (state.settings?.servicenotification) {
        otherPermissions.push(BackendPermissionNames.SERVICE_NOTIFICATION)
    }

    const telSysValue: Definable<number> = state?.permissions?.telephonyserver
    const telSysPerms: Definable<RolePermissions> = state?.administrator?.rolepermissions?.telServer

    if (
        state.settings?.authnotifications &&
        telSysValue === ALL_VALUE &&
        !!telSysPerms?.Read &&
        !!telSysPerms?.Create &&
        !!telSysPerms?.Update
    ) {
        otherPermissions.push(BackendPermissionNames.AUTH_NOTIFICATIONS)
    }

    const userPerms: Definable<RolePermissions> = state?.administrator?.rolepermissions?.applicationUser
    const devicePerms: Definable<RolePermissions> = state?.administrator?.rolepermissions?.devices
    const eptAccessPerms: Definable<RolePermissions> = state?.administrator?.rolepermissions?.eptAccess

    if (
        state.settings?.newdevicenotifications &&
        telSysValue === ALL_VALUE &&
        ((!!userPerms?.Create && !!userPerms?.Update && !!devicePerms?.Read) || !!eptAccessPerms?.Allowed)
    ) {
        otherPermissions.push(BackendPermissionNames.NEW_DEVICE_NOTIFICATIONS)
    }

    return otherPermissions
}
